import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Bits";
import { money, date, canSeeCost } from "@/lib/format";
import { CustomerForm, CUSTOMER_ADMIN } from "@/components/CustomerPicker";
import { SuperDelete } from "@/components/SuperDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, ArrowLeft, Mail, Phone, MapPin, User } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/customers").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const shown = rows.filter((c) => !q || `${c.name} ${c.contact_person || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Customers" subtitle="Every job is linked to a customer">
        {CUSTOMER_ADMIN.includes(user.role) && <Button onClick={() => setOpen(true)} data-testid="new-customer-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />New Customer</Button>}
      </PageHeader>
      <Input placeholder="Search name, contact, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search" className="w-full sm:w-72 mb-4" />
      {shown.length === 0 ? <Empty text={rows.length ? "No customers match your search." : "No customers yet."} /> : (
        <div className="card-industrial overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3 hidden md:table-cell">Contact</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Email / Phone</th><th className="text-right px-4 py-3">Jobs</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shown.map((c) => (
                <tr key={c.id} onClick={() => nav(`/customers/${c.id}`)} data-testid={`customer-row-${c.id}`} className="cursor-pointer hover:bg-accent/30 transition-colors">
                  <td className="px-4 py-3 font-semibold">{c.name}<div className="text-xs text-muted-foreground md:hidden">{c.contact_person}</div></td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{c.contact_person || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num" data-testid={`customer-jobs-${c.id}`}>{c.job_count}{c.open_jobs > 0 && <span className="text-xs text-primary ml-1">({c.open_jobs} open)</span>}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><SuperDelete entity="customer" id={c.id} label={c.name} onDone={load} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CustomerForm open={open} onOpenChange={setOpen} onSaved={(c) => nav(`/customers/${c.id}`)} />
    </div>
  );
}

export function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return null;
  const cost = canSeeCost(user.role);
  const info = [[User, c.contact_person], [Mail, c.email], [Phone, c.phone], [MapPin, c.address]].filter(([, v]) => v);
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="customer-back"><ArrowLeft className="w-4 h-4" />All customers</Link>
      <PageHeader title={c.name} subtitle={`${c.jobs.length} job(s) · ${c.quotes.length} quote(s)`}>
        {CUSTOMER_ADMIN.includes(user.role) && <Button variant="outline" onClick={() => setEdit(true)} data-testid="customer-edit-button"><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
      </PageHeader>
      <div className="card-industrial p-5 grid sm:grid-cols-2 gap-3 text-sm" data-testid="customer-info">
        {info.length === 0 && <span className="text-muted-foreground">No contact details yet.</span>}
        {info.map(([Icon, v], i) => <div key={i} className="flex gap-2 whitespace-pre-line"><Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />{v}</div>)}
        {c.notes && <div className="sm:col-span-2 text-muted-foreground whitespace-pre-line border-t border-border pt-3" data-testid="customer-notes">{c.notes}</div>}
      </div>
      <Section title="Jobs" empty="No jobs for this customer yet." rows={c.jobs} testid="customer-jobs-list" render={(j) => (
        <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`customer-job-${j.job_number}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-accent/30 transition-colors">
          <div className="min-w-0"><span className="font-semibold">{j.job_name || j.job_number}</span> <span className="font-mono-num text-[10px] text-muted-foreground">{j.job_number}</span><div className="text-xs text-muted-foreground">{j.job_type} · Due {date(j.due_date)}</div></div>
          <div className="text-right text-xs shrink-0"><div className="uppercase font-mono">{j.status}</div>{cost && <div className="font-mono-num">{money(j.quoted_value)}</div>}</div>
        </Link>)} />
      {cost && <Section title="Quotations" empty="No quotations for this customer." rows={c.quotes} testid="customer-quotes-list" render={(qt) => (
        <div key={qt.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`customer-quote-${qt.quote_number}`}>
          <div className="min-w-0"><span className="font-mono-num text-primary font-bold">{qt.quote_number}</span><div className="text-xs text-muted-foreground truncate">{qt.description}</div></div>
          <div className="text-right text-xs shrink-0"><div className="uppercase font-mono">{qt.status}</div><div className="font-mono-num">{money(qt.quoted_value)}</div></div>
        </div>)} />}
      <CustomerForm open={edit} onOpenChange={setEdit} initial={c} onSaved={load} />
    </div>
  );
}

function Section({ title, rows, empty, render, testid }) {
  return (
    <div>
      <h2 className="font-heading uppercase font-bold text-base md:text-lg mb-2">{title}</h2>
      {rows.length === 0 ? <Empty text={empty} /> : <div className="card-industrial divide-y divide-border" data-testid={testid}>{rows.map(render)}</div>}
    </div>
  );
}
